import { useEffect, useCallback, useRef } from 'react';
import { DEFAULT_FILL_OPACITY } from '../../../utils/constants/Map';
import PropTypes from 'prop-types';

const Rectangle = ({
  map,
  maps,
  editable,
  draggable,
  color,
  isFilled,
  strokeWeight,
  setValues,
  strokeOpacity,
  paths,
}) => {
  const fitBounds = useCallback(() => {
    const bounds = new maps.LatLngBounds();
    for (const marker of paths) {
      bounds.extend(new maps.LatLng(marker.lat, marker.lng));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths]);

  const updatePolygonBounds = polygon => {
    const bounds = [];
    const polygonBounds = polygon.getPath();
    for (let i = 0; i < polygonBounds.length; i += 1) {
      const vertice = {
        lat: polygonBounds.getAt(i).lat(),
        lng: polygonBounds.getAt(i).lng(),
      };

      bounds.push(vertice);
    }
    setValues([
      {
        isFilled: true,
        color,
        paths: bounds,
        draggable,
        editable,
        strokeOpacity,
        strokeWeight,
      },
    ]);
  };

  const rectangle = new maps.Polygon({
    map: map,
    strokeColor: color,
    strokeOpacity,
    strokeWeight,
    fillColor: color,
    fillOpacity: isFilled ? DEFAULT_FILL_OPACITY : 0,
    draggable,
    editable,
    suppressUndo: true,
  });

  const ref = useRef({
    initialLatLng: null,
  });

  const updatePolygonColor = useCallback(() => {
    rectangle.setOptions({ fillColor: color });
  }, [color, rectangle]);

  useEffect(() => {
    rectangle.setPath(paths);

    const dragStart = rectangle.addListener('dragstart', e => {
      ref.current.initialLatLng = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        path: rectangle
          .getPath()
          .getArray()
          .map(item => item.toJSON()),
      };
    });

    const drag = rectangle.addListener('drag', e => {
      const angle = rectangle.distanceAngle(
        {
          lat: ref.current.initialLatLng.lat,
          lng: ref.current.initialLatLng.lng,
        },
        { lat: e.latLng.lat(), lng: e.latLng.lng() },
      );

      rectangle.setPaths([ref.current.initialLatLng.path]);

      const center = rectangle.getCenter();
      rectangle.rotate(angle, center, map);
    });

    const dragEnd = rectangle.addListener('dragend', () => {
      setValues([
        {
          isFilled: true,
          color,
          paths: rectangle
            .getPath()
            .getArray()
            .map(item => ({ lat: item.lat(), lng: item.lng() })),
          draggable,
          editable,
          strokeOpacity,
          strokeWeight,
        },
      ]);
    });

    const insertAtEvent = maps.event.addListener(
      rectangle.getPath(),
      'insert_at',
      () => updatePolygonBounds(rectangle),
    );

    return () => {
      maps.event.removeListener(dragStart);
      maps.event.removeListener(drag);
      maps.event.removeListener(dragEnd);
      maps.event.removeListener(insertAtEvent);

      rectangle.setMap(null);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paths,
    color,
    draggable,
    editable,
    map,
    maps.event,
    rectangle,
    setValues,
  ]);

  useEffect(() => {
    fitBounds();
    updatePolygonColor();
  }, [fitBounds, updatePolygonColor]);
  return null;
};
Rectangle.propTypes = {
  maps: PropTypes.object,
  editable: PropTypes.bool,
  draggable: PropTypes.bool,
  color: PropTypes.string,
  isFilled: PropTypes.bool,
  strokeWeight: PropTypes.number,
  setValues: PropTypes.func,
  strokeOpacity: PropTypes.number,
  paths: PropTypes.array,
  map: PropTypes.object,
};
export default Rectangle;
